import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["questionKey"];
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import getUsageTracker from 'pricing-pages-lib/utils/getUsageTracker';
import { createToObject } from 'pricing-pages-lib/utils/queryParamUtils';
import { PPUIPageHeader } from 'pricing-pages-ui-components/semantic/PageHeader';
import { useCallback, useEffect } from 'react';
import { Redirect } from 'react-router-dom';
import { BUYER_INTENT, DATA_ENRICHMENT, FORM_SHORTENING } from 'self-service-api/constants/Features';
import BannerSection from '../components/banners/BannerSection';
import ProductPageSubtitle from '../components/common/ProductPageSubtitle';
import ProductPageTitle from '../components/common/ProductPageTitle';
import FailedLoadBanner from '../components/common/loading/FailedLoadBanner';
import FallbackErrorBanner from '../components/common/loading/FallbackErrorBanner';
import { useErrorBoundary } from '../components/error-boundary/useErrorBoundary';
import withErrorBoundary from '../components/error-boundary/withErrorBoundary';
import Faq from '../components/faqs/Faq';
import FooterMicrocopy from '../components/faqs/FooterMicrocopy';
import Question from '../components/faqs/Question';
import { ERROR_SEVERITY } from '../components/error-boundary/error-severity';
import KeyFeatures from '../components/key-features/KeyFeatures';
import HeaderLayoutWrapper from '../components/layout/HeaderLayoutWrapper';
import BreezeIntelligencePackInfo from '../components/merch-card/BreezeIntelligencePackInfo';
import { BREEZE_INTELLIGENCE_PAGE } from '../constants/ErrorBoundaryNames';
import { OWNERSHIP_CHECK_FAILED, PRICING_AND_PURCHASING_FAILED_STATUS } from '../constants/ErrorCauses';
import { FaqsConfig } from '../constants/faqs/FaqsConfig';
import { NavItemEnum } from '../constants/routing/NavItems';
import { useAnchorLinkScroll } from '../hooks/htmlElementHooks';
import { useGlobalLoading } from '../hooks/loadingHooks';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useRedirectIfIneligible } from '../hooks/useRedirectIfIneligible';
import { useTrackRetail } from '../hooks/useTrackRetail';
import { useAppSelector } from '../reducers';
import { isUngatedForBreezeIntelligenceSelector } from '../selectors/GatingSelectors';
import { hasGatesLoadedSelector, hasOnlyOwnershipCheckFailedSelector, hasPricingAndPurchasingFailedSelector } from '../selectors/LoadingStateSelectors';
import { trackPageView } from '../utils/tracking/trackingEventUtils';
import { useQueryParamTerm, useQueryParamsEffect } from '../utils/urls/appQueryParamUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const features = [DATA_ENRICHMENT, BUYER_INTENT, FORM_SHORTENING];
const BreezeIntelligencePage = () => {
  useAnchorLinkScroll();
  useTrackRetail();
  useDocumentTitle('breeze-intelligence');
  const navItem = NavItemEnum.BREEZE_INTELLIGENCE;
  const faqsConfig = FaqsConfig[navItem];
  const isUngatedForBreezeIntelligence = useAppSelector(isUngatedForBreezeIntelligenceSelector);
  const isAppReady = useGlobalLoading();
  const hasPricingAndPurchasingFailed = useAppSelector(hasPricingAndPurchasingFailedSelector);
  const hasOnlyOwnershipCheckFailed = useAppSelector(hasOnlyOwnershipCheckFailedSelector);
  const hasGatesLoaded = useAppSelector(hasGatesLoadedSelector);
  const {
    queryParamTerm
  } = useQueryParamTerm();
  useQueryParamsEffect({
    term: queryParamTerm
  });
  const {
    showBoundary
  } = useErrorBoundary();

  /**
   * On public, there's a race condition where pricing and purchasing has succeeded, but the activeFeatureFlags have not been populated yet (@see PublicMetaDataActions). So sometimes this condition gets evaluated to true and redirects to /suite before we actually confirm if the user is gated for Breeze Intelligence.
   */
  useRedirectIfIneligible({
    condition: useCallback(() => {
      if (!hasGatesLoaded) {
        return;
      }
      return !isUngatedForBreezeIntelligence;
    }, [hasGatesLoaded, isUngatedForBreezeIntelligence])
  });
  useEffect(() => {
    getUsageTracker().setProperties({
      defaultedTab: '',
      tab: ''
    });
    trackPageView({
      screen: navItem
    });
  }, [navItem]);
  if (hasPricingAndPurchasingFailed) {
    if (hasOnlyOwnershipCheckFailed) {
      showBoundary(new Error(OWNERSHIP_CHECK_FAILED));
    }
    showBoundary(new Error(PRICING_AND_PURCHASING_FAILED_STATUS));
  }
  if (!isAppReady || !hasGatesLoaded) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {
      minHeight: 200,
      size: "md",
      grow: true
    });
  }
  return /*#__PURE__*/_jsxs(HeaderLayoutWrapper, {
    navItem: navItem,
    navMarkerName: "BREEZE_INTELLIGENCE_PAGE",
    children: [/*#__PURE__*/_jsx(BannerSection, {
      screen: navItem
    }), /*#__PURE__*/_jsx(FailedLoadBanner, {
      hasFailed: hasPricingAndPurchasingFailed
    }), /*#__PURE__*/_jsx(FallbackErrorBanner, {}), /*#__PURE__*/_jsxs(PPUIPageHeader, {
      children: [/*#__PURE__*/_jsx(ProductPageTitle, {
        nonProductIdentifier: navItem
      }), /*#__PURE__*/_jsx(ProductPageSubtitle, {
        nonProductIdentifier: navItem
      })]
    }), /*#__PURE__*/_jsx(BreezeIntelligencePackInfo, {
      screen: navItem
    }), /*#__PURE__*/_jsx(KeyFeatures, {
      features: features
    }), /*#__PURE__*/_jsxs(Faq, {
      children: [faqsConfig.map(_ref => {
        let {
            questionKey
          } = _ref,
          passthroughOptions = _objectWithoutPropertiesLoose(_ref, _excluded);
        return /*#__PURE__*/_jsx(Question, Object.assign({
          questionKey: questionKey
        }, passthroughOptions), questionKey);
      }), /*#__PURE__*/_jsx(FooterMicrocopy, {})]
    })]
  });
};
export default withErrorBoundary(BreezeIntelligencePage, {
  fallback: /*#__PURE__*/_jsx(Redirect, {
    to: createToObject('/suite')
  }),
  errorBoundaryName: BREEZE_INTELLIGENCE_PAGE,
  severity: ERROR_SEVERITY.HIGH
});